/* eslint-disable complexity */
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { propTypesDataCTA } from './CTA.proptypes';

import Button, { ButtonAppearance } from '../../../../ui/Atoms/Button';
import Icon, { IconSize } from '../../../../ui/Atoms/Icon';
import Text from '../../../../ui/Atoms/Text';

import { getIcon } from '../../../utils/getIcon';
import { linkResolver } from '../../../utils/linkResolver';
import { addTrackingParamsToUrl } from '../../../utils/trackingParams';

import { Color } from '../../../constants/Colors';
import { DocumentType } from '../../../constants/Enums';
import * as S from './CTA.styles';

export const CTASlice = ({ slice, documentType }) => {
  if (!slice) {
    return null;
  }

  const data = slice.primary || {};

  const {
    cta_slice_id: id,
    cta_icon: iconName,
    cta_title: ctaTitle,
    cta_description: { html: ctaDescrition } = {},
    cta_label: ctaLabel,
    data_track_attribute: dataTrackAttribute,
    cta_link: { lang, type, uid, url, target } = {},
    cta_label_optional: optionalCTALabel,
    cta_link_optional: {
      url: optionalLinkUrl,
      target: optionalLinkTarget,
      uid: optionalLinkUid,
      lang: optionalLinkLang,
      type: optionalLinkType,
    } = {},
    data_track_attribute_optional: optionalDataTrackAttribute,
    theme: isWooflashTheme,
  } = data || '';

  if (!url || !ctaLabel) {
    Sentry.captureMessage('Error: missing required data in CTA slice');
    return null;
  }

  const prismicDocument = {
    type,
    lang,
    uid,
  };

  const hasBigTitle =
    documentType === DocumentType.PAGE ||
    documentType === DocumentType.TESTIMONIAL;

  const isWebsitePage = documentType === DocumentType.PAGE;
  const hasBackground = !!ctaTitle && !!ctaDescrition && !isWebsitePage;
  const iconVariation = !!iconName && iconName !== 'None' && isWebsitePage;

  const hasHorizontalBar =
    isWebsitePage &&
    !iconVariation &&
    documentType !== DocumentType.TESTIMONIAL;

  const optionalDocument = {
    type: optionalLinkType,
    lang: optionalLinkLang,
    uid: optionalLinkUid,
  };

  const ctaLink = lang && type && uid ? linkResolver(prismicDocument) : url;
  const optionalCtaLink =
    optionalLinkLang && optionalLinkType && optionalLinkUid
      ? linkResolver(optionalDocument)
      : optionalLinkUrl;

  const [urls, setUrls] = useState({
    ctaUrl: ctaLink,
    optionalCtaUrl: optionalCtaLink,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setUrls({
        ctaUrl: addTrackingParamsToUrl(ctaLink),
        optionalCtaUrl: addTrackingParamsToUrl(optionalCtaLink),
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleCookieChange = () => {
      setUrls({
        ctaUrl: addTrackingParamsToUrl(ctaLink),
        optionalCtaUrl: addTrackingParamsToUrl(optionalCtaLink),
      });
    };
    document.addEventListener('cookiechange', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
    };
  }, []);

  const hasSecondButton = !!optionalCTALabel && !!optionalCtaLink;
  const theme = isWooflashTheme ? 'wooflash' : 'wooclap';

  return (
    <S.Wrapper
      documentType={documentType}
      hasBackground={hasBackground}
      iconVariation={iconVariation}
      id={id}
    >
      {hasHorizontalBar && <S.HorizontalLine />}
      {iconVariation && (
        <S.IconWrapper>
          <Icon
            customColor={Color.WHITE}
            hasGradientBackground={true}
            size={IconSize.BIG}
            theme={theme}
          >
            {getIcon(iconName)}
          </Icon>
        </S.IconWrapper>
      )}
      <S.Content hasBigTitle={hasBigTitle}>
        {ctaTitle && !hasBigTitle && <S.Title>{ctaTitle}</S.Title>}
        {ctaTitle && hasBigTitle && <S.BigTitle>{ctaTitle}</S.BigTitle>}
        {ctaDescrition && <Text>{`<p>${ctaDescrition}</p>`}</Text>}
        <S.ButtonWrapper hasSecondButton={hasSecondButton}>
          <Button
            dataTrackAttribute={dataTrackAttribute}
            link={urls?.ctaUrl}
            target={target}
            theme={theme}
          >
            {ctaLabel}
          </Button>
          {hasSecondButton && (
            <Button
              appearance={ButtonAppearance.OUTLINE}
              dataTrackAttribute={optionalDataTrackAttribute}
              link={urls?.optionalCtaUrl}
              target={optionalLinkTarget}
            >
              {optionalCTALabel}
            </Button>
          )}
        </S.ButtonWrapper>
      </S.Content>
    </S.Wrapper>
  );
};

CTASlice.propTypes = {
  /**
   * Current document type.
   */
  documentType: PropTypes.string,
  /**
   * Content of the CTA slice.
   */
  slice: PropTypes.shape(propTypesDataCTA),
};
